import { AssistantRuntimeProvider, useAui } from "@assistant-ui/react";
import {
  AssistantChatTransport,
  useChatRuntime,
} from "@assistant-ui/react-ai-sdk";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineSparkles } from "react-icons/hi2";
import {
  TbLayoutSidebarLeftExpand,
  TbLayoutSidebarRightExpand,
} from "react-icons/tb";
import { Outlet, useNavigate } from "react-router-dom";

import { Button } from "../../../../components";
import {
  hideIntercomButton,
  showIntercomButton,
} from "../../../../utils/intercom";
import {
  CustomInsightsChatProvider,
  useCustomInsightsChat,
} from "./CustomInsightsChatProvider";
import CustomInsightsFiltersProvider from "./CustomInsightsFiltersProvider";
import CustomInsightsSidebar from "./CustomInsightsSidebar";
import { SavedReportsProvider } from "./SavedReportsContext";

const ASSISTANT_V3_API = "/assistant-v3";

function CustomInsightsPageLayoutContent() {
  const aui = useAui();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  useEffect(() => {
    hideIntercomButton();
    return () => showIntercomButton();
  }, []);

  useEffect(() => {
    return () => {
      try {
        aui.thread().cancelRun();
      } catch {
        // Thread may not have an active run during unmount; safe to ignore.
      }
    };
  }, [aui]);

  return (
    <SavedReportsProvider>
      <CustomInsightsFiltersProvider>
        <CustomInsightsChatProvider>
          <Flex direction="column" w="100%">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              bg="white"
              borderBottom="1px solid"
              borderColor="gray.200"
              padding="0 32px 0 20px"
              minH="56px"
            >
              <Flex alignItems="center" gap={2}>
                <Icon
                  as={
                    isSidebarExpanded
                      ? TbLayoutSidebarRightExpand
                      : TbLayoutSidebarLeftExpand
                  }
                  boxSize={6}
                  cursor="pointer"
                  onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                />
                <Text fontSize="24px" fontWeight="600">
                  Insights Explorer
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <NewExplorationButton />
              </Flex>
            </Flex>

            <Flex h="calc(100vh - 56px)">
              <CustomInsightsSidebar isExpanded={isSidebarExpanded} />
              <Box flex="1" overflow="auto">
                <Outlet />
              </Box>
            </Flex>
          </Flex>
        </CustomInsightsChatProvider>
      </CustomInsightsFiltersProvider>
    </SavedReportsProvider>
  );
}

function NewExplorationButton() {
  const { newExploration } = useCustomInsightsChat();
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    newExploration();
    navigate("/insights/custom");
  }, [newExploration, navigate]);
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      <Icon boxSize={4} as={HiOutlineSparkles} />
      <Text ml="2">New exploration</Text>
    </Button>
  );
}

export default function CustomInsightsPageLayout() {
  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: ASSISTANT_V3_API,
        credentials: "include",
      }),
    []
  );
  const runtime = useChatRuntime({ transport });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <CustomInsightsPageLayoutContent />
    </AssistantRuntimeProvider>
  );
}
